import { z } from 'zod';

export const RatecardLineSchema = z.object({
  serviceRoleId: z.string().uuid(),
  rate: z.number(),
});
export type RatecardLine = z.infer<typeof RatecardLineSchema>;

/**
 * A ratecard line as returned on read responses — the stored line plus the
 * service role's display labels, resolved server-side so the Details table
 * never client-joins a capped role list. Distinct from `RatecardLineSchema`
 * (the write/input shape) so these labels are never persisted into the
 * stored `lines` JSON. `null` = the role was deleted.
 */
export const RatecardLineViewSchema = RatecardLineSchema.extend({
  serviceRoleName: z.string().nullable().optional(),
  serviceRoleShortCode: z.string().nullable().optional(),
});
export type RatecardLineView = z.infer<typeof RatecardLineViewSchema>;

export const RatecardSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string(),
  effectiveFrom: z.string(),
  effectiveTo: z.string().nullable(),
  lines: z.array(RatecardLineViewSchema),
  /** users.id of the operator who recorded this card. Null for rows written
   *  before the column existed. */
  createdBy: z.string().uuid().nullable(),
  /** Display name of `createdBy`, resolved server-side. Null when the creator
   *  is unset or no longer resolvable. */
  createdByName: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Ratecard = z.infer<typeof RatecardSchema>;

// ---- Queries ----------------------------------------------------------------

export const SORT_FIELDS = ['name', 'effectiveFrom', 'createdAt'] as const;
export type RatecardSortField = (typeof SORT_FIELDS)[number];

export interface ListRatecardsQuery {
  q?: string;
  sortField?: RatecardSortField;
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

// ---- Inputs -----------------------------------------------------------------

const ratecardCommon = z.object({
  name: z.string().min(1),
  effectiveFrom: z.string(),
  effectiveTo: z.string().nullable().optional(),
  lines: z.array(RatecardLineSchema).min(1),
});

export const CreateRatecardInput = ratecardCommon;
export type CreateRatecardInput = z.infer<typeof CreateRatecardInput>;

// In-place edit. `expectedUpdatedAt` is the `updatedAt` the client last read —
// an optimistic-concurrency token the server checks to reject a stale overwrite.
export const UpdateRatecardInput = ratecardCommon.extend({
  expectedUpdatedAt: z.string().optional(),
});
export type UpdateRatecardInput = z.infer<typeof UpdateRatecardInput>;
